import { useCallback, useEffect, useState } from 'react';
import { PLANBASE_CONFIG } from '../config';

export interface OrbiSession {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  messages?: unknown[];
}

/**
 * Module-level cache so page + sidebar share the same list without refetching.
 * Simple pub/sub — no external state library required.
 */
let cachedSessions: OrbiSession[] = [];
let inflight: Promise<OrbiSession[]> | null = null;
const subscribers = new Set<(next: OrbiSession[]) => void>();

function notify() {
  for (const cb of subscribers) cb(cachedSessions);
}

function authHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'X-Project-ID': PLANBASE_CONFIG.projectId,
    Authorization: `Bearer ${PLANBASE_CONFIG.apiKey}`,
    'X-User-Token': localStorage.getItem('planbase_token') || '',
  };
}

async function fetchSessions(): Promise<OrbiSession[]> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const response = await fetch(
        `${PLANBASE_CONFIG.apiUrl}/sdk/planbase/ai/sessions`,
        { headers: authHeaders() },
      );
      if (!response.ok) return cachedSessions;
      const data = await response.json();
      cachedSessions = Array.isArray(data.sessions) ? data.sessions : [];
      notify();
      return cachedSessions;
    } catch {
      return cachedSessions;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * Reads the cached Orbi session list and refetches when asked.
 * Publishing a new session (from anywhere) can call `upsertSession()` to
 * update the cache in place so the sidebar re-renders immediately.
 */
export function useOrbiSessions() {
  const [sessions, setSessions] = useState<OrbiSession[]>(cachedSessions);

  useEffect(() => {
    subscribers.add(setSessions);
    fetchSessions();
    return () => {
      subscribers.delete(setSessions);
    };
  }, []);

  const refresh = useCallback(() => fetchSessions(), []);

  return { sessions, refresh };
}

/** Update the cached list after saving/loading a session on the page. */
export function upsertSession(session: OrbiSession) {
  cachedSessions = [
    session,
    ...cachedSessions.filter((s) => s.id !== session.id),
  ];
  notify();
}

/** Remove a session locally and from the backend. Optimistic. */
export async function deleteSession(id: string): Promise<boolean> {
  const previous = cachedSessions;
  // Optimistic remove
  cachedSessions = cachedSessions.filter((s) => s.id !== id);
  notify();
  try {
    const response = await fetch(
      `${PLANBASE_CONFIG.apiUrl}/sdk/planbase/ai/sessions/${id}`,
      { method: 'DELETE', headers: authHeaders() },
    );
    if (!response.ok) throw new Error(String(response.status));
    return true;
  } catch {
    // Roll back on failure so the user sees the chat come back.
    cachedSessions = previous;
    notify();
    return false;
  }
}
